import type {
  NavigationGuardNext,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { useStore } from '@/stores'
import { initialize } from '@/router/middlewares/initialize'

/**
 * DTA-949 athletics release gate. Runs the normal `initialize` middleware
 * first (so `checkout_info` — and with it `district.athleticsEnabled` — is
 * loaded), then sends the family to the dashboard when athletics is off:
 *   - no family district has athletics on, or
 *   - the route names a district (`:districtId`) that is off, or
 *   - the route names a student (`:studentAccountId`) whose district is off.
 */
export const athleticsGate = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
): Promise<void> => {
  let proceeded = false
  let forwarded: unknown
  await initialize(to, from, (arg?: unknown) => {
    proceeded = true
    forwarded = arg
  })
  // `initialize` either left for the lobby (never called next) or forwarded
  // something other than a plain continue — honour that untouched.
  if (!proceeded) return
  if (forwarded !== undefined) {
    next(forwarded as Parameters<NavigationGuardNext>[0])
    return
  }

  if (isAthleticsRouteAllowed(to)) {
    next()
  } else {
    next({ name: 'Dashboard', replace: true })
  }
}

const isAthleticsRouteAllowed = (to: RouteLocationNormalizedGeneric): boolean => {
  const store = useStore()
  if (!store.anyAthleticsOn) return false

  const { districtId, studentAccountId } = to.params
  if (districtId !== undefined && !store.athleticsOn(Number(districtId))) return false

  if (studentAccountId !== undefined) {
    const student = store.students.find(s => s.studentAccountId === Number(studentAccountId))
    const family = student
      ? store.families.find(f => f.familyAccountId === student.familyAccountId)
      : undefined
    // An unknown student is left to the page's own not-found handling; a
    // known student in a district with athletics off is turned away.
    if (family && !store.athleticsOn(family.districtId)) return false
  }

  return true
}
